import React, {useMemo} from 'react';

import {
  SetupSectionProps,
  SiteInfoRow,
  SiteShapefileOverride,
  getStateViewDefaults,
  setupWizardApi,
} from 'src/lib/api/setupWizard';
import {
  BOOLEAN_COLUMNS,
  COLUMN_MIN_WIDTHS,
  NUMBER_COLUMNS,
  OUTLET_NAME_COLUMN,
  REMOVE_UPSTREAM_CONNECTIVITY_COLUMN,
  REMOVE_UPSTREAM_CONNECTIVITY_DELIMITER,
  SELECT_OPTIONS,
  SITE_INFO_COLUMNS,
  SITE_NAME_COLUMN,
  SiteInfoColumn,
} from 'src/lib/setupWizardConstants';
import {buttonPrimary, buttonSecondary} from 'src/lib/tailwindClasses';

import {ApiErrorMessage} from './ApiErrorMessage';
import {CliCommandBlock} from './CliCommandBlock';
import {PrerequisiteWarning, SectionErrors} from './SetupStepSection';
import {useSetupMutation} from './useSetupMutation';

// SiteInfoRow values are typed `unknown` (CSV import infers column types
// generically), so a cell that's usually a delimited string can arrive as a
// number or boolean instead.
export function parseRemoveUpstreamConnectivity(value: unknown): string[] {
  return String(value ?? '')
    .split(REMOVE_UPSTREAM_CONNECTIVITY_DELIMITER)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Compact multi-select for a table cell: a single-line trigger that keeps the
 * row height unchanged, plus a checkbox dropdown that floats (position: fixed)
 * so it is not clipped by the table's overflow container.
 */
function MultiSelectCell({
  options,
  selected,
  onChange,
  placeholder = '—',
}: {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{top: number; left: number; width: number} | null>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const openPanel = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos({top: rect.bottom + 4, left: rect.left, width: rect.width});
    setOpen(true);
  };

  React.useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!btnRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onReposition = () => setOpen(false);
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open]);

  const toggle = (name: string) =>
    onChange(selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name]);

  const summary = selected.length ? selected.join(', ') : placeholder;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="w-full min-w-[10rem] truncate text-left text-sm border border-gray-300 rounded px-1 py-0.5 bg-white"
        title={selected.length ? selected.join(', ') : undefined}
        onClick={() => (open ? setOpen(false) : openPanel())}
      >
        <span className={selected.length ? '' : 'text-gray-400'}>{summary}</span>
      </button>
      {open && pos && (
        <div
          ref={panelRef}
          className="fixed z-50 max-h-48 overflow-auto rounded border border-gray-300 bg-white shadow-lg"
          style={{top: pos.top, left: pos.left, minWidth: pos.width}}
        >
          {options.length === 0 ? (
            <div className="px-2 py-1 text-sm text-gray-400">No other sites</div>
          ) : (
            options.map((name) => (
              <label
                key={name}
                className="flex items-center gap-2 whitespace-nowrap px-2 py-1 text-sm hover:bg-gray-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(name)}
                  onChange={() => toggle(name)}
                />
                <span>{name}</span>
              </label>
            ))
          )}
        </div>
      )}
    </>
  );
}

export default function DefineSitesSection({
  orgId,
  projectId,
  payload,
  queryKey,
  stepErrors,
  prerequisiteWarning,
}: SetupSectionProps) {
  const view = useMemo(() => getStateViewDefaults(payload?.data), [payload?.data]);
  const {
    mutate,
    mutatingAction,
    isMutating,
    error: saveError,
    lastCliCommand,
  } = useSetupMutation(queryKey);
  const [rows, setRows] = React.useState<SiteInfoRow[]>(view.siteInfo);

  React.useEffect(() => {
    setRows(view.siteInfo);
  }, [view.siteInfo]);

  const updateCell = (rowIdx: number, col: string, value: unknown) => {
    setRows((prev) => {
      const next = [...prev];
      next[rowIdx] = {...next[rowIdx], [col]: value};
      return next;
    });
  };

  const addRow = () => {
    const blank = {} as SiteInfoRow;
    for (const col of SITE_INFO_COLUMNS) {
      (blank as unknown as Record<string, unknown>)[col] = BOOLEAN_COLUMNS.has(col) ? false : '';
    }
    setRows((prev) => [...prev, blank]);
  };

  const deleteRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [isImportingCsv, setIsImportingCsv] = React.useState(false);
  const [csvError, setCsvError] = React.useState<string | null>(null);

  const handleCsvImport = async () => {
    if (!csvFile) return;
    setIsImportingCsv(true);
    setCsvError(null);
    try {
      const parsed = await setupWizardApi.parseSiteCsv(orgId, projectId, csvFile);
      if (parsed) {
        setRows(parsed);
        setCsvFile(null);
      }
    } catch (e) {
      setCsvError(e instanceof Error ? e.message : 'CSV import failed');
    } finally {
      setIsImportingCsv(false);
    }
  };

  const hasChanges = JSON.stringify(rows) !== JSON.stringify(view.siteInfo);

  // Per-site shapefile overrides
  type DraftOverride = {id: string; siteName: string; downstreamSite: string; file: File | null};
  const [drafts, setDrafts] = React.useState<DraftOverride[]>([]);
  const [uploadingIds, setUploadingIds] = React.useState<Set<string>>(new Set());
  const [uploadErrors, setUploadErrors] = React.useState<Record<string, string>>({});
  const [deletingNames, setDeletingNames] = React.useState<Set<string>>(new Set());

  const siteNames = useMemo(
    () => rows.map((r) => r.name as string).filter(Boolean),
    [rows]
  );
  const overriddenNames = useMemo(
    () => new Set(view.siteShapefileOverrides.map((o) => o.siteName)),
    [view.siteShapefileOverrides]
  );

  const addDraft = () =>
    setDrafts((prev) => [
      ...prev,
      {id: `draft-${Date.now()}`, siteName: '', downstreamSite: '', file: null},
    ]);

  const removeDraft = (id: string) => setDrafts((prev) => prev.filter((d) => d.id !== id));

  const updateDraft = (id: string, patch: Partial<DraftOverride>) =>
    setDrafts((prev) => prev.map((d) => (d.id === id ? {...d, ...patch} : d)));

  const handleUploadDraft = async (draft: DraftOverride) => {
    if (!draft.file || !draft.siteName || !draft.downstreamSite) return;
    setUploadingIds((prev) => new Set(prev).add(draft.id));
    setUploadErrors((prev) => {
      const next = {...prev};
      delete next[draft.id];
      return next;
    });
    try {
      await mutate(`upload-shapefile-${draft.id}`, () =>
        setupWizardApi.uploadSiteShapefile(orgId, projectId, draft.siteName, draft.downstreamSite, draft.file!)
      );
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
    } catch (e) {
      setUploadErrors((prev) => ({
        ...prev,
        [draft.id]: e instanceof Error ? e.message : 'Upload failed',
      }));
    } finally {
      setUploadingIds((prev) => {
        const next = new Set(prev);
        next.delete(draft.id);
        return next;
      });
    }
  };

  const handleDeleteOverride = async (siteName: string) => {
    setDeletingNames((prev) => new Set(prev).add(siteName));
    try {
      await mutate(`delete-shapefile-${siteName}`, () =>
        setupWizardApi.deleteSiteShapefile(orgId, projectId, siteName)
      );
    } finally {
      setDeletingNames((prev) => {
        const next = new Set(prev);
        next.delete(siteName);
        return next;
      });
    }
  };

  const handleSave = async () => {
    await mutate('save-sites', () => setupWizardApi.updateSiteInfo(orgId, projectId, rows));
  };

  const renderCell = (row: SiteInfoRow, rowIdx: number, col: SiteInfoColumn) => {
    const value = row[col];
    if (BOOLEAN_COLUMNS.has(col)) {
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => updateCell(rowIdx, col, e.target.checked)}
        />
      );
    }
    if (NUMBER_COLUMNS.has(col)) {
      return (
        <input
          type="number"
          className="w-28 text-sm border-gray-300 rounded px-1 py-0.5"
          value={(value as number | null) ?? ''}
          onChange={(e) =>
            updateCell(rowIdx, col, e.target.value === '' ? '' : Number(e.target.value))
          }
        />
      );
    }
    const opts = SELECT_OPTIONS[col];
    if (opts) {
      return (
        <select
          className="text-sm border-gray-300 rounded px-1 py-0.5"
          value={(value as string) ?? ''}
          onChange={(e) => updateCell(rowIdx, col, e.target.value || null)}
        >
          <option value="" />
          {opts.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    if (col === REMOVE_UPSTREAM_CONNECTIVITY_COLUMN) {
      // For a local-flow site this names the upstream forecast point(s) above
      // which flow is excluded. Valid choices are other sites defined in this
      // table; delineation later verifies each is actually upstream. Multiple
      // selections are stored as one delimited string.
      const siteNameOptions = rows
        .map((r) => r.name)
        .filter((name): name is string => Boolean(name) && name !== row.name);
      const selected = parseRemoveUpstreamConnectivity(value);
      return (
        <MultiSelectCell
          options={siteNameOptions}
          selected={selected}
          onChange={(next) =>
            updateCell(
              rowIdx,
              col,
              next.length ? next.join(`${REMOVE_UPSTREAM_CONNECTIVITY_DELIMITER} `) : null
            )
          }
        />
      );
    }
    return (
      <input
        className="w-full text-sm border-gray-300 rounded px-1 py-0.5"
        value={(value as string) ?? ''}
        onChange={(e) => updateCell(rowIdx, col, e.target.value || null)}
      />
    );
  };

  return (
    <div className="space-y-4">
      {prerequisiteWarning && <PrerequisiteWarning message={prerequisiteWarning} />}
      <SectionErrors errors={stepErrors} />

      <div className="flex flex-wrap gap-3 items-end border border-gray-200 rounded-md p-3 bg-gray-50">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Import from CSV</label>
          <input
            type="file"
            accept=".csv"
            className="text-sm"
            onChange={(e) => {
              setCsvFile(e.target.files?.[0] ?? null);
              setCsvError(null);
            }}
          />
        </div>
        <button
          type="button"
          disabled={isImportingCsv || !csvFile}
          className={buttonSecondary}
          onClick={handleCsvImport}
        >
          {isImportingCsv ? 'Importing…' : 'Import CSV'}
        </button>
        <ApiErrorMessage message={csvError} />
        <p className="text-xs text-gray-500">
          CSV must have a &quot;{SITE_NAME_COLUMN}&quot; column. Missing columns will be filled with
          defaults. Blank {OUTLET_NAME_COLUMN} values are auto-detected from river topology when
          delineation runs.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr>
              {SITE_INFO_COLUMNS.map((col) => (
                <th
                  key={col}
                  className="border border-gray-300 bg-gray-50 px-2 py-1 text-left text-xs font-medium text-gray-600 whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
              <th className="border border-gray-300 bg-gray-50 px-2 py-1" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="odd:bg-white even:bg-gray-50">
                {SITE_INFO_COLUMNS.map((col) => (
                  <td
                    key={col}
                    className={`border border-gray-200 px-1 py-1 ${COLUMN_MIN_WIDTHS[col] ?? ''}`}
                  >
                    {renderCell(row, rowIdx, col)}
                  </td>
                ))}
                <td className="border border-gray-200 px-1 py-1 text-center">
                  <button
                    type="button"
                    className="text-red-500 hover:text-red-700 text-sm"
                    onClick={() => deleteRow(rowIdx)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex gap-2 items-center">
        <button type="button" className={buttonSecondary} onClick={addRow}>
          + Add row
        </button>
        <button
          type="button"
          disabled={isMutating || !hasChanges}
          className={buttonPrimary}
          onClick={handleSave}
        >
          {mutatingAction === 'save-sites' ? 'Saving…' : 'Save'}
        </button>
        <ApiErrorMessage message={saveError} />
      </div>
      <CliCommandBlock command={lastCliCommand} />

      {/* Shapefile Overrides */}
      <div className="mt-4 border-t border-gray-200 pt-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Shapefile Overrides</h3>
        <p className="text-xs text-gray-500">
          Upload a basin shapefile (.zip, .kml, .gpkg) for specific sites instead of running
          automated delineation. Specify which site the basin outlet flows into.
        </p>

        {/* Saved overrides */}
        {view.siteShapefileOverrides.map((override: SiteShapefileOverride) => (
          <div
            key={override.siteName}
            className="flex flex-wrap items-center gap-3 text-sm bg-gray-50 rounded px-3 py-2"
          >
            <span className="font-medium w-32 truncate" title={override.siteName}>
              {override.siteName}
            </span>
            <span className="text-gray-400 text-xs">flows into</span>
            <span className="w-32 truncate" title={override.downstreamSite}>
              {override.downstreamSite}
            </span>
            {override.hasFile ? (
              <span className="text-green-700 text-xs bg-green-50 rounded px-1.5 py-0.5">
                ✓ shapefile uploaded
              </span>
            ) : (
              <span className="text-amber-700 text-xs bg-amber-50 rounded px-1.5 py-0.5">
                no file
              </span>
            )}
            <button
              type="button"
              disabled={deletingNames.has(override.siteName)}
              className="ml-auto text-red-500 hover:text-red-700 text-sm disabled:opacity-50"
              onClick={() => handleDeleteOverride(override.siteName)}
            >
              {deletingNames.has(override.siteName) ? 'Removing…' : 'Remove'}
            </button>
          </div>
        ))}

        {/* Draft rows */}
        {drafts.map((draft) => {
          const availableForSite = siteNames.filter(
            (n) =>
              (!overriddenNames.has(n) && !drafts.some((d) => d.id !== draft.id && d.siteName === n)) ||
              n === draft.siteName
          );
          const availableForDownstream = siteNames.filter((n) => n !== draft.siteName);
          const isUploading = uploadingIds.has(draft.id);
          const uploadError = uploadErrors[draft.id];
          return (
            <div key={draft.id} className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="text-sm border-gray-300 rounded px-1 py-0.5 w-36"
                  value={draft.siteName}
                  onChange={(e) => updateDraft(draft.id, {siteName: e.target.value})}
                >
                  <option value="">Site…</option>
                  {availableForSite.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span className="text-gray-400 text-xs">flows into</span>
                <select
                  className="text-sm border-gray-300 rounded px-1 py-0.5 w-36"
                  value={draft.downstreamSite}
                  onChange={(e) => updateDraft(draft.id, {downstreamSite: e.target.value})}
                >
                  <option value="">Downstream site…</option>
                  {availableForDownstream.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <input
                  type="file"
                  accept=".zip,.kml,.gpkg"
                  className="text-sm"
                  onChange={(e) => updateDraft(draft.id, {file: e.target.files?.[0] ?? null})}
                />
                <button
                  type="button"
                  disabled={isUploading || !draft.siteName || !draft.downstreamSite || !draft.file}
                  className={buttonPrimary}
                  onClick={() => handleUploadDraft(draft)}
                >
                  {isUploading ? 'Uploading…' : 'Upload'}
                </button>
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-500 text-sm"
                  onClick={() => removeDraft(draft.id)}
                >
                  ✕
                </button>
              </div>
              {uploadError && (
                <p className="text-red-600 text-xs pl-1">{uploadError}</p>
              )}
            </div>
          );
        })}

        <button type="button" className={buttonSecondary} onClick={addDraft}>
          + Add shapefile override
        </button>
      </div>
    </div>
  );
}
